import { Spinner } from '@change/ds-core-ui';

import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import { withPrefetchedData } from '#src/shared/prefetch';

import {
	PetitionGuidesPageContextProvider,
	prefetchPageContext,
} from '#src/app/pages/petitionGuidesArticle/pageContext';
import type { PetitionGuidesPagePrefetchedDataProps } from '#src/app/pages/petitionGuidesArticle/pageContext';
import { isLoaded } from '#src/app/shared/utils/async';

import { Layout } from '../layout';

import { PetitionGuidesErrorHandler } from './ErrorHandler';

export const PetitionGuidesArticle = withPrefetchedData(
	function PetitionGuides({ prefetchedData }: PetitionGuidesPagePrefetchedDataProps) {
		const { slug } = useKnownAvailableParams<{ slug: string }>();

		return (
			<PetitionGuidesPageContextProvider
				slug={slug}
				prefetchedData={prefetchedData}
				errorHandler={PetitionGuidesErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</PetitionGuidesPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context),
		pageTitle: ({ prefetchedData, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return fromTemplate(prefetchedData.pageData.pageData.article.heading);
		},
		pageCanonical: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return `/petition-guides/${prefetchedData.pageData.pageData.article.slug}`;
		},
		pageMetadata: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const {
				article: { subheading, heroImage, heading },
			} = prefetchedData.pageData.pageData;
			return {
				description: subheading ?? undefined,
				ogImage: heroImage?.url,
				ogTitle: heading,
				twitterImage: heroImage?.url,
				twitterImageAlt: heading,
				ogType: 'website' as const,
			};
		},
		pageAlternateUrls: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const {
				article: { localeSlugs },
			} = prefetchedData.pageData.pageData;
			if (!localeSlugs) {
				return undefined;
			}

			const allSlugs: Array<{ locale: string; slug: string }> = [];
			for (const { locales, slug } of localeSlugs) {
				for (const locale of locales) {
					allSlugs.push({ locale, slug });
				}
			}

			const result: Record<string, string> = {};
			const defaultSlug = allSlugs.find(({ locale }) => locale === 'en-US');
			if (defaultSlug) {
				result['en-US'] = `/petition-guides/${defaultSlug.slug}`;
			}
			for (const { locale, slug } of allSlugs) {
				if (!locale.includes('en-')) {
					result[locale] = `/petition-guides/${slug}`;
				}
			}

			return Object.keys(result).length > 0 ? result : undefined;
		},
	},
);
